<script setup lang="ts">
/**
 * The CMS: news, FAQ, the legal documents, and the footer's social links.
 *
 * Four small collections on one screen rather than four rail entries, because
 * each is a handful of rows and a rail of seven where four are near-empty
 * tables reads as a bigger product than this is.
 *
 * **Legal documents are created here, through `LegalEditModal`.** The slug
 * list used to be three fixed values; it is dynamic now (`GET /api/legal`,
 * read by the footer and the profile screen — see `state/legal-slugs.ts`), so
 * an operator can add a fourth document and both public surfaces pick it up
 * without a deploy. News and FAQ each get the same create/edit modal
 * treatment; Social links is `SocialLinksEditor` (inline rows, drag to reorder).
 */
import { ref } from 'vue';

import { adminApi, type FaqRow, type LegalRow, type NewsRow } from './admin-api';
import FaqEditModal from './FaqEditModal.vue';
import LegalEditModal from './LegalEditModal.vue';
import NewsEditModal from './NewsEditModal.vue';
import SocialLinksEditor from './SocialLinksEditor.vue';
import { usePane, when } from './pane';

const news = usePane(() => adminApi.news());
const faq = usePane(() => adminApi.faq());
const legal = usePane(() => adminApi.legal());

/** Which legal document the modal is open on — a row to edit, `'new'` to create, or closed. */
const legalModal = ref<LegalRow | 'new' | null>(null);
function closeLegalModal(): void {
  legalModal.value = null;
}
function onLegalSaved(): void {
  legalModal.value = null;
  void legal.reload();
}

const newsModal = ref<NewsRow | 'new' | null>(null);
function closeNewsModal(): void {
  newsModal.value = null;
}
function onNewsSaved(): void {
  newsModal.value = null;
  void news.reload();
}

const faqModal = ref<FaqRow | 'new' | null>(null);
function closeFaqModal(): void {
  faqModal.value = null;
}
function onFaqSaved(): void {
  faqModal.value = null;
  void faq.reload();
}
</script>

<template>
  <h1>Content</h1>
  <p class="lede">What the public pages say.</p>

  <section>
    <h2>Legal documents</h2>
    <p v-if="legal.error.value" class="bad">{{ legal.error.value }}</p>

    <div class="scroller">
      <table data-test="admin-legal-table">
        <thead>
          <tr><th>Document</th><th>Slug</th><th>State</th><th>Updated</th><th>Actions</th></tr>
        </thead>
        <tbody>
          <tr v-for="row in legal.data.value ?? []" :key="row.id">
            <td>{{ row.title }}</td>
            <td>{{ row.slug }}</td>
            <td>
              <span v-if="row.active" class="chip ok">Published</span>
              <span v-else class="chip warn">Hidden</span>
            </td>
            <td>{{ when(row.updatedAt) }}</td>
            <td class="actions">
              <button @click="legalModal = row">Edit</button>
              <button @click="legal.act(() => adminApi.legalPatch(row.id, { active: !row.active }))">
                {{ row.active ? 'Hide' : 'Publish' }}
              </button>
            </td>
          </tr>
          <tr v-if="!legal.loading.value && (legal.data.value?.length ?? 0) === 0">
            <td colspan="5" class="empty">No documents yet.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="toolbar" style="margin-top: 0.5rem">
      <button class="accent" @click="legalModal = 'new'">+ Add document</button>
    </div>

    <LegalEditModal
      v-if="legalModal"
      :row="legalModal === 'new' ? null : legalModal"
      @close="closeLegalModal"
      @saved="onLegalSaved"
    />
  </section>

  <section>
    <h2>News</h2>
    <p v-if="news.error.value" class="bad">{{ news.error.value }}</p>
    <div class="scroller">
      <table>
        <thead><tr><th>Title</th><th>Slug</th><th>State</th><th>Created</th><th>Actions</th></tr></thead>
        <tbody>
          <tr v-for="row in news.data.value ?? []" :key="row.id">
            <td>{{ row.title }}</td>
            <td>{{ row.slug }}</td>
            <td>
              <span v-if="row.published" class="chip ok">Published</span>
              <span v-else class="chip warn">Draft</span>
            </td>
            <td>{{ when(row.createdAt) }}</td>
            <td class="actions">
              <button @click="newsModal = row">Edit</button>
              <button @click="news.act(() => adminApi.newsPatch(row.id, { published: !row.published }))">
                {{ row.published ? 'Unpublish' : 'Publish' }}
              </button>
            </td>
          </tr>
          <tr v-if="!news.loading.value && (news.data.value?.length ?? 0) === 0">
            <td colspan="5" class="empty">No posts yet.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="toolbar" style="margin-top: 0.5rem">
      <button class="accent" @click="newsModal = 'new'">+ Add article</button>
    </div>

    <NewsEditModal
      v-if="newsModal"
      :row="newsModal === 'new' ? null : newsModal"
      @close="closeNewsModal"
      @saved="onNewsSaved"
    />
  </section>

  <section>
    <h2>FAQ</h2>
    <p v-if="faq.error.value" class="bad">{{ faq.error.value }}</p>
    <div class="scroller">
      <table>
        <thead><tr><th>Question</th><th>State</th><th>Actions</th></tr></thead>
        <tbody>
          <tr v-for="row in faq.data.value ?? []" :key="row.id">
            <td>{{ row.question }}</td>
            <td>
              <span v-if="row.active" class="chip ok">Live</span>
              <span v-else class="chip warn">Hidden</span>
            </td>
            <td class="actions">
              <button @click="faqModal = row">Edit</button>
              <button @click="faq.act(() => adminApi.faqPatch(row.id, { active: !row.active }))">
                {{ row.active ? 'Hide' : 'Show' }}
              </button>
              <button class="danger" @click="faq.act(() => adminApi.faqDelete(row.id))">Delete</button>
            </td>
          </tr>
          <tr v-if="!faq.loading.value && (faq.data.value?.length ?? 0) === 0">
            <td colspan="3" class="empty">No entries.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="toolbar" style="margin-top: 0.5rem">
      <button class="accent" @click="faqModal = 'new'">+ Add</button>
    </div>

    <FaqEditModal
      v-if="faqModal"
      :row="faqModal === 'new' ? null : faqModal"
      @close="closeFaqModal"
      @saved="onFaqSaved"
    />
  </section>

  <SocialLinksEditor />
</template>
